import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  fetchOperatorMonthly, registerOperatorPayment, deleteOperatorPayment,
  type OperatorMonthlySummary, type OperatorMonthlyShare, type OperatorOption,
} from '../api/commissions'
import { Card, CardHeader, CardContent, DataTable, LoadingState, Callout, Button, TextField, DateField, useConfirm } from './ui'

function eur(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function monthLabel(m: string): string {
  const [y, mo] = m.split('-').map(Number)
  return new Date(y, mo - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
}

function range(): { from: string; to: string } {
  const now = new Date()
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  return { from: fmt(new Date(now.getFullYear(), now.getMonth() - 11, 1)), to: fmt(now) }
}

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function PaymentForm({ month, operatorId, share, onSaved, onCancel }: {
  month: string
  operatorId: number | null | undefined
  share: OperatorMonthlyShare
  onSaved: () => void
  onCancel: () => void
}) {
  const residuo = Math.max(share.remaining ?? 0, 0)
  const [paidAt, setPaidAt] = useState(todayIso())
  const [amount, setAmount] = useState(residuo.toFixed(2))
  const [method, setMethod] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    const value = Number(amount.replace(',', '.'))
    if (!Number.isFinite(value) || value <= 0) { setErr('Importo non valido.'); return }
    setBusy(true); setErr(null)
    try {
      await registerOperatorPayment({
        month, beneficiary: share.beneficiary, operatorId: operatorId ?? undefined, amount: Math.round(value * 100) / 100, paidAt,
        method: method.trim() || undefined, note: note.trim() || undefined,
      })
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore')
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', padding: '8px 0' }}>
      <DateField kind="date" label="Data" value={paidAt} onChange={e => setPaidAt(e.target.value)} />
      <TextField label="Importo (€)" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} />
      <TextField label="Metodo" value={method} onChange={e => setMethod(e.target.value)} />
      <TextField label="Nota" value={note} onChange={e => setNote(e.target.value)} />
      <Button variant="filled" loading={busy} onClick={() => void save()}>Registra pagamento</Button>
      <Button variant="text" disabled={busy} onClick={onCancel}>Annulla</Button>
      {err && <Callout tone="error">{err}</Callout>}
    </div>
  )
}

const SOURCE_LABEL: Record<string, string> = {
  statement: 'Estratto PDF', payments: 'Pagamenti operatore', none: 'Nessun dato',
}

/** Riepilogo mensile per tutti gli operatori: mesi (piu' recente in alto), gruppi operatore espandibili con le quote per beneficiario. */
export function OperatorMonthlyCard() {
  const [months, setMonths] = useState<OperatorMonthlySummary[] | null>(null)
  const [operators, setOperators] = useState<OperatorOption[]>([])
  const [operatorId, setOperatorId] = useState<number | undefined>(undefined)
  const [includeEmpty, setIncludeEmpty] = useState(false)
  const [canPay, setCanPay] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [openForm, setOpenForm] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const confirm = useConfirm()

  useEffect(() => {
    const { from, to } = range()
    fetchOperatorMonthly(from, to, { operatorId, includeEmpty })
      .then(r => { setMonths(r.months); setOperators(r.operators ?? []); setCanPay(r.canRecordPayments === true) })
      .catch(e => setError(e instanceof Error ? e.message : 'Errore'))
  }, [reloadKey, operatorId, includeEmpty])

  const removePayment = async (id: number) => {
    if (!(await confirm({ title: 'Elimina pagamento', message: 'Eliminare il pagamento registrato?', confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await deleteOperatorPayment(id)
      setReloadKey(k => k + 1)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    }
  }

  const byMonth = useMemo(() => {
    const g = new Map<string, OperatorMonthlySummary[]>()
    for (const m of months ?? []) g.set(m.month, [...(g.get(m.month) ?? []), m])
    return [...g.entries()]
  }, [months])

  const toggle = (k: string) => setExpanded(prev => {
    const n = new Set(prev)
    if (!n.delete(k)) n.add(k)
    return n
  })

  if (error) return <Callout tone="error">{error}</Callout>
  if (months === null) return <LoadingState />
  if (months.length === 0 && !operatorId && !includeEmpty) return null

  return (
    <Card>
      <CardHeader title="Riepilogo mensile operatori" subtitle="Incassato per operatore e ripartizione per beneficiario" icon="table_chart" />
      <CardContent>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <label>
            Operatore{' '}
            <select value={operatorId ?? ''} onChange={e => setOperatorId(e.target.value ? Number(e.target.value) : undefined)}>
              <option value="">Tutti</option>
              {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <label>
            <input type="checkbox" checked={includeEmpty} onChange={e => setIncludeEmpty(e.target.checked)} /> Mostra anche operatori senza dati
          </label>
        </div>
        {months.length === 0 && <Callout tone="info">Nessun dato nel periodo.</Callout>}
        {byMonth.map(([month, items]) => (
          <div key={month} style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '8px 0' }}>
              {monthLabel(month)} · totale {eur(items.reduce((a, x) => a + (x.grossTotal ?? 0), 0))}
            </h4>
            <DataTable>
              <thead>
                <tr>
                  <th>Operatore</th>
                  <th>Fonte</th>
                  <th className="md-num">Incassato</th>
                  <th className="md-num">Non dettagliato</th>
                </tr>
              </thead>
              <tbody>
                {items.map(m => {
                  const gk = `${month}|${m.operatorId ?? m.operator}`
                  const open = expanded.has(gk)
                  return (
                    <Fragment key={gk}>
                      <tr onClick={() => toggle(gk)} style={{ cursor: 'pointer' }}>
                        <td><strong>{open ? '▾' : '▸'} {m.operator}</strong></td>
                        <td><small className="text-muted">{SOURCE_LABEL[m.source ?? 'statement']}</small></td>
                        <td className="md-num">{eur(m.grossTotal)}</td>
                        <td className="md-num">
                          {m.unexplained !== null && Math.abs(m.unexplained) >= 0.005 ? eur(m.unexplained) : '—'}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={4}>
                            {m.shares.length === 0 ? <small className="text-muted">Nessun beneficiario.</small> : (
                              <DataTable>
                                <thead>
                                  <tr>
                                    <th>Beneficiario</th>
                                    <th className="md-num">Lordo</th>
                                    <th className="md-num">Netto</th>
                                    <th className="md-num">Pagato</th>
                                    <th className="md-num">Residuo</th>
                                    <th>Pagamenti</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {m.shares.map(s => {
                                    const key = `${gk}|${s.beneficiary}`
                                    return (
                                      <tr key={key}>
                                        <td>{s.beneficiary}</td>
                                        <td className="md-num">{eur(s.gross)}</td>
                                        <td className="md-num">{eur(s.net)}</td>
                                        <td className="md-num">{eur(s.paid)}</td>
                                        <td className="md-num">{eur(s.remaining)}</td>
                                        <td>
                                          {s.payments.map(p => (
                                            <div key={p.id}>
                                              <small>
                                                {new Date(p.paidAt).toLocaleDateString('it-IT')} · {eur(p.amount)}
                                                {p.method ? ` · ${p.method}` : ''}{p.note ? ` · ${p.note}` : ''}
                                              </small>
                                              {canPay && <Button variant="text" size="sm" onClick={() => void removePayment(p.id)}>Elimina</Button>}
                                            </div>
                                          ))}
                                          {canPay && (openForm === key
                                            ? <PaymentForm month={month} operatorId={m.operatorId} share={s} onCancel={() => setOpenForm(null)}
                                                onSaved={() => { setOpenForm(null); setReloadKey(k => k + 1) }} />
                                            : <Button variant="text" size="sm" onClick={() => setOpenForm(key)}>Registra pagamento</Button>)}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </DataTable>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </DataTable>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
